/**
 * Provincia AR — derivada de la VERDAD ÚNICA de location: el Worker
 * (__TEMPLATE_CONFIG__ / cookies → useTemplateSelection).
 *
 * Este hook NO detecta nada por su cuenta. La detección client-side anterior
 * (timezone / IP services) era una verdad paralela que contradecía al Worker:
 * America/Argentina/Buenos_Aires cubre CABA y media Argentina, así que todo
 * caía en "BA" — incluida CABA, que para el Worker es una provincia distinta.
 *
 * Devuelve el provinceCode del catálogo de templates (argentinaProvinces.ts:
 * "CABA", "BA", "CB", …) mapeado desde el nombre completo que resuelve el
 * Worker ("CABA", "Buenos Aires", "Córdoba", …).
 */

import { useMemo } from "react";
import { useTemplateSelection } from "@/hooks/useTemplateSelection";
import {
  getArgentinaProvinceCodeByName,
  getArgentinaProvinceConfig,
} from "@/config/argentinaProvinces";

interface ProvinceState {
  province: string | null;
  provinceName: string | null;
  isLoading: boolean;
  error: string | null;
  detectionMethod: string | null;
}

export function useArgentinaProvince(country?: string | null): ProvinceState {
  const { country: workerCountry, province: workerProvince } = useTemplateSelection();
  const effectiveCountry = country ?? workerCountry;

  return useMemo(() => {
    if (effectiveCountry !== "AR" || !workerProvince) {
      return {
        province: null,
        provinceName: null,
        isLoading: false,
        error: null,
        detectionMethod: null,
      };
    }
    const code = getArgentinaProvinceCodeByName(workerProvince);
    const cfg = code ? getArgentinaProvinceConfig(code) : null;
    return {
      province: code,
      provinceName: cfg?.provinceName ?? workerProvince,
      isLoading: false,
      error: null,
      detectionMethod: code ? "worker_config" : "worker_unmapped",
    };
  }, [effectiveCountry, workerProvince]);
}

/** Alias de compatibilidad: la variante "sync" ya no difiere (no hay async). */
export const useArgentinaProvinceSync = useArgentinaProvince;

export default {
  useArgentinaProvince,
  useArgentinaProvinceSync,
};
